import type { ReactNode } from "react";
import { MarkdownContent } from "@/components/shared/Markdown/MarkdownContent";
import { LinkMultipleFilled } from "@fluentui/react-icons";
import type { ISectionDto } from "@/types/filteredContentTypes";
import { useTranslations } from "@/hooks/useTranslations";
import styles from "./Section.module.scss";

interface ISectionProps {
  section: ISectionDto;
  sectionId: string;
  copiedSectionId: string | null;
  onCopyLink: (sectionId: string) => void;
  mediaBaseUrl?: string;
  isEngagementDetailsPage?: boolean;
}

export function Section({ section, sectionId, copiedSectionId, onCopyLink, mediaBaseUrl, isEngagementDetailsPage }: ISectionProps): ReactNode {
  const { t } = useTranslations();

  const sectionClassName = [
    styles.section,
    isEngagementDetailsPage ? styles.engagementDetailsSection : undefined,
  ]
    .filter((c): c is string => Boolean(c))
    .join(" ");
  
  return (
    <section
      key={section.key}
      id={sectionId}
      className={sectionClassName}
    >
      <div className={styles.sectionHeader}>
        <button
          type="button"
          className={styles.copyLinkButton}
          onClick={(): void => onCopyLink(sectionId)}
          aria-label={`${t("contentPage.copyLink")} ${section.title}`}
          title={copiedSectionId === sectionId ? t("contentPage.linkCopied") : t("contentPage.copyLink")}
        >
          <LinkMultipleFilled className={styles.linkIcon} />
        </button>
        <p className="font-size-h2 font-weight-semibold">{section.title}</p>
      </div>
      {section.content && (
        <MarkdownContent content={section.content} mediaBaseUrl={mediaBaseUrl} />
      )}
    </section>
  );
}
